import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { getUserProfilePicture, getUserProfilePictureAlternative, getExchangeProfilePicture } from '../utils/graphApi';

interface ProfilePictureProps {
  size?: number;
  className?: string;
  fallbackIcon?: string;
  showBorder?: boolean;
  borderColor?: string;
  onClick?: () => void;
  userEmail?: string; // Email of the user whose profile picture to fetch
}

const ProfilePicture: React.FC<ProfilePictureProps> = ({
  size = 40,
  className = '',
  fallbackIcon = '👤',
  showBorder = true,
  borderColor = '#e5e7eb',
  onClick,
  userEmail
}) => {
  const { instance } = useMsal();
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchProfilePicture = async () => {
      try {
        console.log(`🖼️ ProfilePicture component: Starting to fetch profile picture for ${userEmail || 'current user'}...`);
        console.log(`📧 User email being used: "${userEmail}"`);
        setLoading(true);
        setError(false);
        
        // Only try to fetch profile picture if userEmail is provided
        let picture = null;
        if (userEmail) {
          console.log(`🔄 Attempting to fetch profile picture for specific user: ${userEmail}`);
          
          // Try Exchange Online first (where Office 365 profile pictures are often stored)
          picture = await getExchangeProfilePicture(instance, userEmail);
          
          // If Exchange method fails, try the alternative method (tries multiple endpoints)
          if (!picture) {
            console.log('🔄 Exchange method failed, trying alternative method...');
            picture = await getUserProfilePictureAlternative(instance, userEmail);
          }
          
          // If alternative method fails, try the original method
          if (!picture) {
            console.log('🔄 Alternative method failed, trying original method...');
            picture = await getUserProfilePicture(instance, userEmail);
          }
        } else {
          console.log('🔄 No userEmail provided, will show fallback icon');
        }
        
        console.log(`🖼️ ProfilePicture component: Received picture for ${userEmail || 'current user'}:`, picture ? 'SUCCESS' : 'NULL');
        if (picture) {
          console.log(`🖼️ ProfilePicture component: Picture data preview: ${picture.substring(0, 50)}...`);
        }
        setProfilePicture(picture);
      } catch (err) {
        console.error(`🖼️ ProfilePicture component: Error fetching profile picture for ${userEmail || 'current user'}:`, err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProfilePicture();
  }, [instance, userEmail]);

  const containerStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f3f4f6',
    border: showBorder ? `2px solid ${borderColor}` : 'none',
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'all 0.2s ease',
    position: 'relative',
  };

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
  };

  const fallbackStyle: React.CSSProperties = {
    fontSize: size * 0.5,
    color: '#6b7280',
  };

  if (loading) {
    return (
      <div 
        style={containerStyle} 
        className={className}
        onClick={onClick}
      >
        <img 
          src="/Avid Logo Mark.png" 
          alt="Avid Logo" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            opacity: 0.6, // Slightly faded to indicate loading state
          }}
        />
      </div>
    );
  }

  if (error || !profilePicture) {
    return (
      <div 
        style={containerStyle} 
        className={className}
        onClick={onClick}
      >
        <img 
          src="/Avid Logo Mark.png" 
          alt="Avid Logo" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
          onError={() => {
            // If the logo fails to load, fall back to the emoji icon
            console.log('🖼️ Avid logo failed to load, using emoji fallback');
          }}
        />
      </div>
    );
  }

  return (
    <div 
      style={containerStyle} 
      className={className}
      onClick={onClick}
    >
      <img 
        src={profilePicture} 
        alt="Profile" 
        style={imageStyle}
        onError={() => setError(true)}
      />
    </div>
  );
};

export default ProfilePicture;
